import { useCallback, useEffect, useState } from "react";
import { Video } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLive } from "@/lib/live";
import { FormDialog } from "@/components/kids/kidsUi";
import { CallRoom } from "./CallRoom";

const detail = (e) => e?.response?.data?.detail?.message || errMsg(e);

function RecordedNotice({ onJoin, onCancel }) {
  return (
    <FormDialog open onClose={onCancel} title="This call is being recorded" description="Everyone on the call will see that you joined. The recording goes to your family's call archive." testid="call-recorded-notice">
      <div className="flex gap-2">
        <button className="q-btn q-btn-ghost min-h-[48px] flex-1" onClick={onCancel} data-testid="call-recorded-cancel">Cancel</button>
        <button className="q-btn q-btn-primary min-h-[48px] flex-1" onClick={onJoin} data-testid="call-recorded-join">Join</button>
      </div>
    </FormDialog>
  );
}

// "Live call · <n> people · Join" on every page for Open calls I may join.
function JoinBar({ live, onJoin }) {
  if (!live.length) return null;
  const c = live[0];
  return (
    <div className="fixed inset-x-0 bottom-3 z-[80] mx-auto flex w-[min(94vw,460px)] items-center gap-3 rounded-full bg-emerald-600 py-1.5 pl-4 pr-1.5 text-white shadow-2xl" data-testid="call-join-bar">
      <Video className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate text-sm font-semibold" data-testid="call-join-bar-text">Live call · {c.count} {c.count === 1 ? "person" : "people"}{c.recording ? " · ● Recording" : ""}</span>
      <button className="min-h-[44px] rounded-full bg-white px-5 font-bold text-emerald-700" onClick={() => onJoin(c)} data-testid="call-join-bar-btn">Join</button>
    </div>
  );
}

export function CallLayer({ hub, enabled }) {
  const { subscribe } = useLive();
  const [ring, setRing] = useState(null);
  const [room, setRoom] = useState(null);
  const [live, setLive] = useState([]);
  const [notice, setNotice] = useState(null);
  const base = `/organizations/${hub?.id}/connect`;
  const loadLive = useCallback(() => api.get(`${base}/calls/live`).then(({ data }) => setLive(data.calls)).catch(() => {}), [base]);
  const leave = useCallback((post) => { setRoom((r) => { if (r && post) api.post(`${base}/calls/${r.id}/leave`).catch(() => {}); return null; }); setTimeout(loadLive, 500); }, [base, loadLive]);
  const join = async (c) => {
    setNotice(null);
    try { const { data } = await api.post(`${base}/calls/${c.id}/join`); setRoom({ id: c.id, url: data.url, token: data.token }); setLive([]); } catch (e) { toast.error(detail(e)); }
  };
  const accept = async () => {
    try { const { data } = await api.post(`${base}/calls/${ring.call_id}/accept`); setRoom({ id: ring.call_id, ...data }); setRing(null); setNotice(null); } catch (e) { toast.error(detail(e)); setRing(null); }
  };
  useEffect(() => {
    if (!enabled || !hub) return undefined;
    loadLive();
    const t = setInterval(loadLive, 15000);
    const start = async (e) => { try { const { data } = await api.post(`${base}/calls`, e.detail); setRoom({ id: data.call.id, url: data.url, token: data.token }); } catch (x) { toast.error(detail(x)); } };
    window.addEventListener("queue:call", start);
    const off = subscribe((m) => {
      if (m.entity !== "call") return;
      if (m.action === "live") loadLive();
      if (m.action === "ringing") api.get(`${base}/calls/${m.payload.call_id}/state`).then(({ data }) => setRing({ ...m.payload, recording: !!data.recording })).catch(() => setRing(m.payload));
      if (["missed", "ended", "declined"].includes(m.action)) setRing((r) => (r?.call_id === m.payload.call_id ? null : r));
    });
    return () => { clearInterval(t); window.removeEventListener("queue:call", start); off(); };
  }, [enabled, base, subscribe, hub, loadLive]);
  if (!enabled || !hub) return null;
  return (
    <>
      {ring && !room && (
        <div className="fixed inset-x-0 top-4 z-[95] mx-auto w-[min(92vw,380px)] rounded-2xl bg-zinc-900 p-4 text-white shadow-2xl" data-testid="call-ringing">
          <p className="font-display text-lg font-extrabold">{ring.from} is calling…</p>
          {ring.recording && <p className="mt-1 text-sm font-semibold text-red-300" data-testid="call-ringing-recorded">This call is being recorded</p>}
          <div className="mt-3 flex gap-2">
            <button className="q-btn min-h-[44px] flex-1 bg-red-600 text-white" onClick={() => { api.post(`${base}/calls/${ring.call_id}/decline`); setRing(null); }} data-testid="call-decline-btn">Decline</button>
            <button className="q-btn min-h-[44px] flex-1 bg-emerald-600 text-white" onClick={accept} data-testid="call-accept-btn">Accept</button>
          </div>
        </div>
      )}
      {!room && <JoinBar live={live} onJoin={(c) => (c.recording ? setNotice(c) : join(c))} />}
      {notice && <RecordedNotice onJoin={() => join(notice)} onCancel={() => setNotice(null)} />}
      {room && <CallRoom hub={hub} call={room} onLeave={leave} />}
    </>
  );
}
